import type { BoxProps } from '@mui/material/Box';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import type { FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';

export interface MenuOptionI<T = unknown> {
  callback: (params?: T) => void;
  optionText: string;
  optionIcon?: ReactNode;
  disabled?: boolean;
}

interface BaseMenuI {
  icon: ReactNode;
  menuItems: MenuOptionI[];
  boxProps?: BoxProps;
}

const BaseMenu: FC<BaseMenuI> = ({ icon, menuItems, boxProps }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  // to handle menu close in Pendo
  useEffect(() => {
    window.closeUserMenu = handleClose;
    return () => {
      // to cleanup if component unmounts
      window.closeUserMenu = undefined;
    };
  }, []);

  return (
    <>
      <Box {...boxProps}>
        <IconButton
          onClick={handleClick}
          size="small"
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          id="pendo-menu"
        >
          {icon}
        </IconButton>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="base-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {menuItems.map((option, index) => {
          return (
            <MenuItem
              key={index}
              onClick={(): void => {
                handleClose();
                option.callback();
              }}
              disabled={option.disabled}
            >
              {option.optionIcon}
              {option.optionText}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default BaseMenu;
